"use client"

import Title from "@/components/common/Typography/Title"
import { useIsDesktop } from "@/hooks/app/useIsDesktop"
import { useTour } from "@/hooks/tour/useTour"
import { TypeTourContent } from "@/lib/redux/features/tour/types"
import { removeVietnameseChars } from "@/utils/string/removeVietnameseChars"
import { Card, Col, Row } from "antd"
import { FC, useMemo } from "react"
import { breakdownChars } from "../TourContentConditions"

type Bucket = "includes" | "excludes" | "terms"

const classifyCondition = (title: string): Bucket => {
  const normalized = removeVietnameseChars(title).toLowerCase()
  if (normalized.includes("khong bao gom")) return "excludes"
  if (normalized.includes("bao gom")) return "includes"
  return "terms"
}

const formatDescription = (description: string) =>
  breakdownChars.reduce(
    (str, char) =>
      str
        .replace(char, str[0] === char ? "- " : "")
        .replaceAll(char, "<br><br> -"),
    description
  )

type TourDetailConditionSectionsProps = {
  bucket: Bucket
  sectionId: string
  title: string
}

const TourDetailConditionSections: FC<TourDetailConditionSectionsProps> = ({
  bucket,
  sectionId,
  title,
}) => {
  const { tour: { data: tour } } = useTour()
  const isDesktop = useIsDesktop()

  const items = useMemo(
    () =>
      (tour?.contentConditions || []).filter(
        (cond: TypeTourContent) =>
          !!cond.description && classifyCondition(cond.title) === bucket
      ),
    [tour?.contentConditions, bucket]
  )

  if (!items.length) return null

  return (
    <Col id={sectionId} span={24}>
      <Card
        className="tour-detail__section"
        style={isDesktop ? {} : { borderRadius: 0 }}
        styles={{ body: { padding: "1.2rem 1.5rem" } }}
      >
        <Title level={4} className="tour-detail__section-title">
          {title}
        </Title>
        <Row gutter={[0, 16]}>
          {items.map((item: TypeTourContent) => (
            <Col key={item.title} span={24}>
              {items.length > 1 && (
                <Title level={5} style={{ marginBottom: "0.5rem" }}>
                  {item.title}
                </Title>
              )}
              <div
                className="tour-detail__condition--content"
                dangerouslySetInnerHTML={{
                  __html: formatDescription(item.description),
                }}
              />
            </Col>
          ))}
        </Row>
      </Card>
    </Col>
  )
}

export default TourDetailConditionSections
